import { useEffect, useRef, useState, useCallback } from 'react';
import './EarthquakeAnimation.css';

/**
 * EARTHQUAKE ANIMATION - Ground-type Move
 * No sprite images - pure screen and Pokémon shaking effects
 * 
 * Animation Sequence:
 * 1. PREPARATION: Player Pokémon preparation stance
 * 2. RUMBLE_START: Initial ground tremors begin
 * 3. EARTHQUAKE_MAIN: Full earthquake with intense shaking
 * 4. AFTERSHOCK: Diminishing tremors
 * 5. SETTLE: Everything settles back to normal
 * 
 * Effects: Screen shake, both Pokémon movement, ground-type visual effects
 */
export default function EarthquakeAnimation({ playerRef, opponentRef, onComplete }) {
 
  const [animationPhase, setAnimationPhase] = useState('idle');
  const timersRef = useRef([]);
  const originalStylesRef = useRef({});
  
  // IMMEDIATE TEST - Apply shake as soon as component mounts
  useEffect(() => {
  
    
    // Create and inject CSS directly
    const shakeCSS = `
      @keyframes testEarthquakeShake {
        0% { transform: translateX(0px) translateY(0px); }
        25% { transform: translateX(-10px) translateY(-8px); }
        50% { transform: translateX(8px) translateY(-10px); }
        75% { transform: translateX(-8px) translateY(6px); }
        100% { transform: translateX(0px) translateY(0px); }
      }
      .test-earthquake-shake {
        animation: testEarthquakeShake 100ms ease-in-out infinite !important;
      }
    `;
    
    const styleElement = document.createElement('style');
    styleElement.id = 'earthquake-test-styles';
    styleElement.textContent = shakeCSS;
    document.head.appendChild(styleElement);
    
    // Apply shake to body immediately
    document.body.classList.add('test-earthquake-shake');

    
    // Remove after 3 seconds
    const testTimer = setTimeout(() => {
      document.body.classList.remove('test-earthquake-shake');
      const testStyle = document.getElementById('earthquake-test-styles');
      if (testStyle) testStyle.remove();

    }, 3000);
    
    return () => {
      clearTimeout(testTimer);
      document.body.classList.remove('test-earthquake-shake');
      const testStyle = document.getElementById('earthquake-test-styles');
      if (testStyle) testStyle.remove();
    };
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
  
    
    // Clear all timers
    timersRef.current.forEach(timer => clearTimeout(timer));
    timersRef.current = [];
    
    // Reset player styles
    if (playerRef.current && originalStylesRef.current.player) {
      playerRef.current.style.transition = originalStylesRef.current.player.transition || '';
      playerRef.current.style.transform = originalStylesRef.current.player.transform || '';
      playerRef.current.style.filter = originalStylesRef.current.player.filter || '';
      playerRef.current.classList.remove('earthquake-shake', 'earthquake-hit');
    }
    
    // Reset opponent styles
    if (opponentRef.current && originalStylesRef.current.opponent) {
      opponentRef.current.style.transition = originalStylesRef.current.opponent.transition || '';
      opponentRef.current.style.transform = originalStylesRef.current.opponent.transform || '';
      opponentRef.current.style.filter = originalStylesRef.current.opponent.filter || '';
      opponentRef.current.classList.remove('earthquake-shake', 'earthquake-hit');
    }
    
    // Reset screen shake from all possible containers
    const battleContainer = playerRef.current?.closest('.battle-container') || 
                           playerRef.current?.closest('.battle-scene') || 
                           playerRef.current?.closest('.game-container') || 
                           playerRef.current?.parentElement || 
                           document.body;
    
    const battleScene = document.querySelector('.battle-scene') || 
                       document.querySelector('.battle-container') || 
                       document.querySelector('.game-container') || 
                       battleContainer;
    
    battleContainer.classList.remove('earthquake-screen-shake');
    if (battleScene !== battleContainer) {
      battleScene.classList.remove('earthquake-screen-shake');
    }
    
    const parentContainer = battleContainer.parentElement;
    if (parentContainer && parentContainer !== document.body) {
      parentContainer.classList.remove('earthquake-screen-shake');
    }
    
    // Remove fallback body shake
    document.body.classList.remove('earthquake-screen-shake');
    
    // Remove fallback styles
    const fallbackStyle = document.getElementById('earthquake-fallback-shake');
    if (fallbackStyle) {
      fallbackStyle.remove();
    }
    
   
  }, [playerRef, opponentRef]);

  // Store original styles
  const storeOriginalStyles = useCallback(() => {
    if (playerRef.current) {
      originalStylesRef.current.player = {
        transition: playerRef.current.style.transition,
        transform: playerRef.current.style.transform,
        filter: playerRef.current.style.filter,
      };
    }
    
    if (opponentRef.current) {
      originalStylesRef.current.opponent = {
        transition: opponentRef.current.style.transition,
        transform: opponentRef.current.style.transform,
        filter: opponentRef.current.style.filter,
      };
    }
  }, [playerRef, opponentRef]);

  // Create ground crack visual effects
  const createGroundEffects = useCallback(() => {
    if (!opponentRef.current) return;
    
    const container = opponentRef.current.parentElement;
    if (!container) return;
    
   
    // Create multiple ground crack lines
    for (let i = 0; i < 5; i++) {
      const crack = document.createElement('div');
      crack.className = 'earthquake-ground-crack';
      
      // Random positioning across the battle field
      const randomX = Math.random() * 80 + 10; // 10-90% width
      const randomY = Math.random() * 60 + 20; // 20-80% height
      const randomRotation = Math.random() * 60 - 30; // -30 to 30 degrees
      const randomLength = Math.random() * 40 + 60; // 60-100px length
      
      crack.style.left = `${randomX}%`;
      crack.style.top = `${randomY}%`;
      crack.style.transform = `translate(-50%, -50%) rotate(${randomRotation}deg)`;
      crack.style.width = `${randomLength}px`;
      crack.style.setProperty('--delay', `${i * 100}ms`);
      
      container.appendChild(crack);
      
      // Remove crack after animation
      const removeTimer = setTimeout(() => {
        if (crack.parentNode) {
          crack.parentNode.removeChild(crack);
        }
      }, 2000);
      
      timersRef.current.push(removeTimer);
    }
  }, [opponentRef]);

  // Create dust cloud effects
  const createDustClouds = useCallback(() => {
    if (!opponentRef.current) return;
    
    const container = opponentRef.current.parentElement;
    if (!container) return;
    
   
    // Create multiple dust clouds
    for (let i = 0; i < 8; i++) {
      const dust = document.createElement('div');
      dust.className = 'earthquake-dust-cloud';
      
      // Random positioning
      const randomX = Math.random() * 90 + 5; // 5-95% width
      const randomY = Math.random() * 70 + 15; // 15-85% height
      const randomScale = Math.random() * 0.5 + 0.5; // 0.5-1.0 scale
      
      dust.style.left = `${randomX}%`;
      dust.style.top = `${randomY}%`;
      dust.style.transform = `translate(-50%, -50%) scale(${randomScale})`;
      dust.style.setProperty('--delay', `${i * 150}ms`);
      
      container.appendChild(dust);
      
      // Remove dust after animation
      const removeTimer = setTimeout(() => {
        if (dust.parentNode) {
          dust.parentNode.removeChild(dust);
        }
      }, 3000);
      
      timersRef.current.push(removeTimer);
    }
  }, [opponentRef]);

  // Main animation execution
  const executeAnimation = useCallback(() => {
    
    if (!playerRef || !playerRef.current) {
     
      // Try to continue anyway with screen shake only
    }
    
    if (!opponentRef || !opponentRef.current) {
      
      // Try to continue anyway with screen shake only
    }

 
    
    // Apply contained screen shake to battle area only
    
    const initialBattleContainer = playerRef.current?.closest('.battle-container') || 
                                  playerRef.current?.closest('.battle-scene') || 
                                  playerRef.current?.closest('.game-container') || 
                                  playerRef.current?.parentElement;
    
    if (initialBattleContainer && initialBattleContainer !== document.body) {
      initialBattleContainer.classList.add('earthquake-screen-shake');
      initialBattleContainer.style.animation = 'earthquakeScreenShake 80ms ease-in-out infinite';
      
      // Remove shake after 2 seconds
      setTimeout(() => {
        initialBattleContainer.classList.remove('earthquake-screen-shake');
        initialBattleContainer.style.animation = '';
       
      }, 2000);
    }
    
    if (playerRef.current) {
      storeOriginalStyles();
    }
    setAnimationPhase('preparation');

    // Find ONLY the battle container - NO document.body fallback
    const battleContainer = playerRef.current?.closest('.battle-container') || 
                           playerRef.current?.closest('.battle-scene') || 
                           playerRef.current?.closest('.game-container') || 
                           playerRef.current?.parentElement;
    
    // Debug logging
    
    if (playerRef.current) {
      playerRef.current.style.transition = 'transform 150ms ease-in-out';
      playerRef.current.style.transform = 'scale(1.05) translateY(-3px)'; // Slight lift

    }

    const quickTimer = setTimeout(() => {
      
      
      // ONLY shake individual Pokémon sprites - NO container shake
      if (playerRef.current) {
        playerRef.current.classList.add('earthquake-shake');
      
      }
      if (opponentRef.current) {
        opponentRef.current.classList.add('earthquake-shake');
        opponentRef.current.classList.add('earthquake-hit');
      }
      
      // NO CONTAINER SHAKE - prevents battle screen cutoff
      
      
      // NO VISUAL EFFECTS - prevents any layout disruption
      
      // END ANIMATION QUICKLY - Remove all effects after short duration
      const endTimer = setTimeout(() => {
        
        setAnimationPhase('settle');
        
        // Remove all effects - NO container shake to remove
        
        if (playerRef.current) {
          playerRef.current.classList.remove('earthquake-shake');
          playerRef.current.style.transform = ''; // Reset position
        }
        if (opponentRef.current) {
          opponentRef.current.classList.remove('earthquake-shake', 'earthquake-hit');
        }
        
        // Complete animation quickly
        setTimeout(() => {
          cleanup();
          onComplete();
        }, 200);
        
      }, 800); // Short 800ms total duration
      
      timersRef.current.push(endTimer);
    }, 200); // Quick 200ms preparation
    
    timersRef.current.push(quickTimer);
  }, [playerRef, opponentRef, onComplete, storeOriginalStyles, createGroundEffects, createDustClouds, cleanup]);

  // Start animation on mount
  useEffect(() => {
    
    
    const delayTimer = setTimeout(() => {
      executeAnimation();
    }, 1000); // Delay to let the immediate test run first
    
    // Cleanup on unmount
    return () => {
      clearTimeout(delayTimer);
      cleanup();
    };
  }, [executeAnimation, cleanup]);

  return null; // This component only handles DOM manipulation
}
